import { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/store';
import type { RootState } from '@/store';
import { fetchChantiers } from '@/store/thunks/chantierThunks';
import { fetchNotAffectedCiternes } from '@/store/thunks/citerneThunks';
import { createAffectation, updateAffectation } from '@/store/thunks/affectationThunks';
import type { CiterneAffectation } from '@/types';
import type { Chantier } from '@/types';
import type { Citerne } from '@/types';
import { Input, Button, Select } from '@/components/ui';

const schema = Yup.object({
  chantierId: Yup.string().required('Chantier obligatoire'),
  citerneId: Yup.string().required('Citerne obligatoire'),
  date_d: Yup.string().required('Date début obligatoire'),
  date_f: Yup.string().required('Date fin obligatoire'),
});

interface CiterneAffectationFormProps {
  affectation?: CiterneAffectation | null;
  type: 'create' | 'update';
  onClose: () => void;
  onSuccess?: () => void;
}

export default function CiterneAffectationForm({ affectation, type, onClose, onSuccess }: CiterneAffectationFormProps) {
  const dispatch = useAppDispatch();
  const chantiers = useAppSelector((s: RootState) => s.chantier.chantiers);
  const notAffectedCiternes = useAppSelector((s: RootState) => s.citerne.notAffectedCiternes);

  useEffect(() => {
    dispatch(fetchChantiers());
    dispatch(fetchNotAffectedCiternes());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      chantierId: affectation?.chantier?.id ? String(affectation.chantier.id) : '',
      citerneId: affectation?.citerne?.id ? String(affectation.citerne.id) : '',
      date_d: affectation?.date_d ? (affectation.date_d as string).slice(0, 10) : '',
      date_f: affectation?.date_f ? (affectation.date_f as string).slice(0, 10) : '',
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      const chantier = chantiers.find((c: Chantier) => String(c.id) === values.chantierId);
      const citerne = notAffectedCiternes.find((c: Citerne) => String(c.id) === values.citerneId);
      if (!chantier || !citerne) return;
      const payload = {
        chantier: { id: chantier.id, code: chantier.code },
        citerne: { id: citerne.id, code: citerne.code },
        date_d: values.date_d,
        date_f: values.date_f,
      };
      if (type === 'update' && affectation?.id) {
        const res = await dispatch(updateAffectation({ ...affectation, ...payload }));
        if (updateAffectation.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await dispatch(createAffectation(payload));
        if (createAffectation.fulfilled.match(res)) {
          onSuccess?.();
          onClose();
        }
      }
    },
  });

  const chantierOptions = chantiers.map((c: Chantier) => ({ value: String(c.id), label: c.code ?? String(c.id) }));
  const citerneOptions = notAffectedCiternes.map((c: Citerne) => ({ value: String(c.id), label: c.code ?? String(c.id) }));

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <Select
        label="Chantier"
        options={chantierOptions}
        value={formik.values.chantierId}
        onChange={(e) => formik.setFieldValue('chantierId', e.target.value)}
        onBlur={formik.handleBlur}
        error={formik.touched.chantierId ? formik.errors.chantierId : undefined}
      />
      <Select
        label="Citerne"
        options={citerneOptions}
        value={formik.values.citerneId}
        onChange={(e) => formik.setFieldValue('citerneId', e.target.value)}
        onBlur={formik.handleBlur}
        error={formik.touched.citerneId ? formik.errors.citerneId : undefined}
      />
      <Input
        label="Date début"
        type="date"
        id="date_d"
        {...formik.getFieldProps('date_d')}
        error={formik.touched.date_d ? formik.errors.date_d : undefined}
      />
      <Input
        label="Date fin"
        type="date"
        id="date_f"
        {...formik.getFieldProps('date_f')}
        error={formik.touched.date_f ? formik.errors.date_f : undefined}
      />
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" loading={formik.isSubmitting}>{type === 'update' ? 'Modifier' : 'Créer'}</Button>
      </div>
    </form>
  );
}
